import React, { useEffect, useState } from "react";
import axios from "axios";
import { Home, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LETTING = ["New to holiday lets", "Already letting (self-managed)", "Already letting (via agent)"];
const empty = { name: "", email: "", phone: "", property_location: "", num_properties: "", property_type: "", currently_letting: "New to holiday lets", notes: "" };

export default function PublicOwnerSignup() {
  const [info, setInfo] = useState(undefined);
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { axios.get(`${API}/public/owner-signup`).then((r) => setInfo(r.data)).catch(() => setInfo(null)); }, []);

  const submit = async () => {
    setSending(true); setErr("");
    try {
      await axios.post(`${API}/public/owner-signup`, { ...form, num_properties: form.num_properties === "" ? null : Number(form.num_properties) });
      setDone(true);
    } catch (e) { setSending(false); setErr("Sorry, we couldn't submit just now. Please try again."); }
  };

  const brand = info?.brand_name || "Bides";
  if (info === undefined) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md bg-[#18181A] flex items-center justify-center"><Home className="h-5 w-5 text-white" /></div>
          <span className="font-head text-xl font-extrabold tracking-tight">{brand}</span>
        </div>
        {done ? (
          <div className="bg-white border border-border rounded-md p-8 text-center" data-testid="owner-signup-thanks">
            <CheckCircle2 className="h-10 w-10 text-[#2B4C3F] mx-auto mb-3" />
            <h1 className="font-head text-2xl font-extrabold">Thanks — we'll be in touch</h1>
            <p className="text-sm text-muted-foreground mt-2">Our team will review your details and contact you shortly about managing your holiday let.</p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-md p-6">
            <p className="overline text-accent">Register your interest</p>
            <h1 className="font-head text-2xl font-extrabold tracking-tight mt-1">Let {brand} manage your holiday let</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-5">Tell us about your property and we'll get in touch about our fully managed holiday-let service.</p>
            <div className="space-y-3">
              <div><Label className="overline">Your name</Label><Input data-testid="owner-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="overline">Email</Label><Input data-testid="owner-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
                <div><Label className="overline">Phone</Label><Input data-testid="owner-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="overline">Property location</Label><Input data-testid="owner-location" value={form.property_location} onChange={(e) => setForm({ ...form, property_location: e.target.value })} className="mt-1.5" placeholder="Town / area" /></div>
                <div><Label className="overline"># of properties</Label><Input data-testid="owner-numprops" type="number" value={form.num_properties} onChange={(e) => setForm({ ...form, num_properties: e.target.value })} className="mt-1.5" placeholder="e.g. 1" /></div>
              </div>
              <div><Label className="overline">Property type</Label><Input data-testid="owner-type" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="mt-1.5" placeholder="e.g. 2-bed coastal cottage" /></div>
              <div>
                <Label className="overline">Currently letting?</Label>
                <Select value={form.currently_letting} onValueChange={(v) => setForm({ ...form, currently_letting: v })}>
                  <SelectTrigger data-testid="owner-letting" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{LETTING.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="overline">Anything else?</Label><Textarea data-testid="owner-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Tell us what you're looking for…" /></div>
              <Button data-testid="owner-submit" className="w-full" disabled={sending || !form.name.trim()} onClick={submit}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Register interest"}
              </Button>
              {err && <p data-testid="owner-error" className="text-xs text-[#9E382A] text-center">{err}</p>}
            </div>
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground mt-4">Powered by {brand}</p>
      </div>
    </div>
  );
}
